import Effects from 'Effects';
import ParameterContext from 'effects/ParameterContext.js';
import {sleep} from 'utils/sleep.js';
import {LFO, ENVELOPE} from 'constants/mod-types.js';
import {uuid} from 'uuidv4';
import Tone from 'tone';
import EffectEdge from './EffectEdge.js';
import {AudioParamPercentageValueParameter,
        AudioParamValueParameter} from 'parameters/AbstractAudioParamParameter.js';
import {saveParameterToJson, loadParameterFromJson}  from 'parameters/parameterJsonFunctions.js';
import {getEffect} from '../EffectsList.js';
import LFOModulator from 'modulators/LFO.js';
import MacroMixin from '../MacroMixin.js';
import ConfigurableRack from './ConfigurableRack.js';
import RackableEffect from './RackableEffect.js';

const INPUT_ID = -1;

class EffectRack extends RackableEffect(ConfigurableRack(MacroMixin(Effects.Effect))) {
    constructor(sequencerEngine) {
        super('Effect Rack',
              () => new Tone.Gain(1),
              sequencerEngine); 
        
        this.inputNode = this.effectNode;
        this.inputNode.x = 0;
        this.inputNode.y = 200;
        this.outputNode = new Tone.Gain(1);
        this.outputNode.id = 'speakers-' + this.id;
        this.outputNode.x = 400;
        this.outputNode.y = 200;

        this.initializeConfigurations(this.adjacencyLists);
    }

    onBPMUpdated() {
        for (let i in this.effects) {
            if (this.effects[i].onBPMUpdated) {
                this.effects[i].onBPMUpdated();
            }
        }
    }

    getParameters() {
        let parameters = super.getParameters();
        for (let i in this.macros) {
            parameters = [
                ... parameters,
                ...this.macros[i].parameters
            ];
        }
        return parameters;
    }

    connect(node) {
        this.outputNode.connect(node);
    }

    disconnect() {
        this.outputNode.disconnect();
    }

    addEffect(effect, setWet=true) {
        super.addEffect(effect, setWet);
        if (setWet) {
            effect.enable(true);
        }
        if (setWet && effect.wetParameter) {
            effect.wetParameter.set(1);
        }
    }

    dispose() {
        this.macros.forEach(
            m => m.dispose());
        this.macros = [];
        this.effects.forEach(s => s.dispose());
        for (let i in this.adjacencyLists) {
            if (this.adjacencyLists[i]) {
                this.adjacencyLists[i].forEach(
                    s => s.dispose());
            }
        }
        this.adjacencyLists = undefined;
        super.dispose();
        this.outputNode.dispose();
        this.outputNode = undefined;
        this.effects = undefined;
        this.macros = undefined;
    }

    deleteEdge(edge) {
        if (!edge.modulator) {
            for (let id in this.adjacencyLists) {
                let adjacencyList = this.adjacencyLists[id];
                let indexOf =  adjacencyList.indexOf(edge); 
                if (indexOf > -1) {
                    if (parseInt(id) === -1) {
                        this.inputNode.disconnect(edge.gain);
                    } else {
                        let effect = this.effects.find(e => e.id === id);
                        if (effect) {
                            effect.disconnect(edge.gain);
                        }
                    }
                }
            }
        }

        super.deleteEdge(edge);
   }

    connectEdge(node1, edge) {
        super.connectEdge(node1, edge);
        node1.connect(edge.gain);
        return edge;
    }

    // if node1 is undefined, then its INPUT
    // if node2 is undefined, then its OUTPUT
    getJson() {
        return {
           ... this.getRackJson(),
            isCollapsed: this.isCollapsed
        };
    }

    muteRack(mute=true) {
        this.inputNode.gain.value = mute ? 0 : 1;
        this.outputNode.gain.value = mute ? 0 : 1;
    }

    fromJson(json, tracks, trackNumber, isCopy) {
        this.muteRack(true);
        super.fromJson(json, isCopy);
        this.isCollapsed = json.isCollapsed;
        this.adjacencyLists = {};
        this.configurations = [];
        this.effects.forEach(
            e => e.dispose());
        this.effects = [];

        let {effects} = json;
        let b = {};
        for (let i in effects) {
            let jsonEffect = effects[i];
            let ret = getEffect(jsonEffect, tracks, this.sequencerEngine, trackNumber);
            let effect = ret.effect;
            this.addEffect(effect, false);
            effect.id = jsonEffect.id
            effect.x = jsonEffect._x || jsonEffect.x;
            effect.y = jsonEffect._y || jsonEffect.y;
            b = {
                ... b,
                ... ret.bulk
            };
        }

        this.fromRackJson(json, isCopy);

        this.muteRack(false);
        this.outputNode.id = 'speakers-' + this.id;
        return b;
    }

    fromJsonSync(json, tracks, trackNumber, effects, sequencerEngine, ignoreId) {
        super.fromJson(json, true);
        this.adjacencyLists = {};
        this.configurations = [];
        this.effects.forEach(
            e => e.dispose());
        this.effects = [];

        let b = {};
        for (let i in effects) {
            let effect = effects[i].getEffectCopy(sequencerEngine, ignoreId);
            this.addEffect(effect, false);
            effect.x = json.effects[i].x;
            effect.y = json.effects[i].y;

            let jsonEffect = effects[i];

            if (effect.id !== jsonEffect.id) {
                for (let config of json.configurations) {
                    let adj = config[jsonEffect.id];
                    config[effect.id] = adj;
                    delete config[jsonEffect.id];
                }
            }
         
            /*
            b = {
                ... b,
                ... ret.bulk
            };
            */
        }

        this.fromRackJson(json, true);
 
        return b;
    }

    getEffectCopy(sequencerEngine=this.sequencerEngine, ignoreId=true) {
        // TODO: differntiate between bounce copy and regular copy (where we'd still need to know
        // about the parameter changes)
        var copy = new EffectRack(sequencerEngine);
        let track = this.context.track;
        copy.fromJsonSync(
            this.getJson(),
            this.sequencerEngine.tracks,
            track !== undefined ? track.trackNumber : 0,
            this.effects,
            sequencerEngine,
            ignoreId
        );
        return copy;
    }
}

export default EffectRack;
